import { PropTypes, React } from 'imports';
import { FormattedResource } from '..';
import Popup, { CloseMode as PopupCloseMode } from '../Popup/Popup';
import { addEvent, deepAssign, isOutEvent, isOverEvent, removeEvent, toArray } from 'utils';
import common from '!!less-vars-loader!static/common.less';
import Enum from 'utils/Enum';
import './DropPanel.less';

export const AUTO_ICON = 'AUTO_ICON';

export const OpenMode = Enum('BUTTON', 'HOVER');

export const CloseMode = { ...PopupCloseMode, BUTTON: 'BUTTON', UNHOVER: 'UNHOVER' };

export const SnapSide = Enum('LEFT', 'MIDDLE', 'RIGHT');

const CONTENT_RESIZE_LIMIT = 200;

export default class DropPanel extends React.Component {
    static get AUTO_ICON() {
        return AUTO_ICON;
    }

    static get SnapSide() {
        return SnapSide;
    }

    static get OpenMode() {
        return OpenMode;
    }

    static get CloseMode() {
        return CloseMode;
    }

    static get propTypes() {
        return {
            opened: PropTypes.bool,
            title: PropTypes.oneOfType([FormattedResource.resourcePropType, PropTypes.node]),
            icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
            openMode: PropTypes.oneOfType([
                PropTypes.oneOf(Object.values(OpenMode)),
                PropTypes.arrayOf(PropTypes.oneOf(Object.values(OpenMode)))
            ]),
            closeMode: PropTypes.oneOfType([
                PropTypes.oneOf(Object.values(CloseMode)),
                PropTypes.arrayOf(PropTypes.oneOf(Object.values(CloseMode)))
            ]),
            hoverTimeout: PropTypes.number,
            unhoverTimeout: PropTypes.number,
            snap: PropTypes.oneOfType([
                PropTypes.oneOf(Object.values(SnapSide)),
                PropTypes.arrayOf(PropTypes.oneOf(Object.values(SnapSide)))
            ]),
            panelOffset: PropTypes.number, // Hack to avoid the Intercom bubble
            arrowed: PropTypes.bool,
            arrowOffset: PropTypes.number,
            dimmed: PropTypes.bool,
            disabled: PropTypes.bool,
            tabIndex: PropTypes.number,
            scrollElementId: PropTypes.string,
            className: PropTypes.string,
            onKeyDown: PropTypes.func,
            onOpen: PropTypes.func,
            onClose: PropTypes.func,
            onClick: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            opened: undefined,
            icon: AUTO_ICON,
            openMode: OpenMode.BUTTON,
            closeMode: [CloseMode.BUTTON, CloseMode.OUTSIDE],
            hoverTimeout: 500,
            unhoverTimeout: 200,
            snap: [SnapSide.LEFT, SnapSide.RIGHT],
            panelOffset: 0,
            arrowed: false,
            dimmed: false,
            disabled: false,
            tabIndex: 0,
            className: ''
        };
    }

    state = {
        firstRenderDone: false,
        opened: false,
        shouldGoDown: true,
        left: null,
        top: 0,
        right: null,
        bottom: null
    };

    componentDidMount() {
        addEvent(window, 'resize', this._onWindowResize);

        this.componentDidUpdate();
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.state.firstRenderDone && this.refs.root) {
            this.setState({ firstRenderDone: true });
        }

        if (this.props.opened === true && this.state.firstRenderDone) {
            this._tryOpen();
        }

        if (this.props.opened === false) {
            this._tryClose();
        }

        if (this._isInToggleList(this.props.snap, SnapSide.MIDDLE) && prevState && !prevState.opened && this.state.opened === true) {
            this._alignPopupToMiddle();
        }

        if (this.props.scrollElementId) {
            const scrollTarget = this.props.scrollElementId === 'document' ? document : document.getElementById(this.props.scrollElementId);

            if (this._scrollTarget && (prevProps.scrollElementId !== this.props.scrollElementId || !scrollTarget || !this.state.opened)) {
                this._scrollTarget = null;
                removeEvent(this._scrollTarget, 'scroll', this._onElementScroll);
            }

            if (!this._scrollTarget && scrollTarget && this.state.opened) {
                this._scrollTarget = scrollTarget;
                addEvent(this._scrollTarget, 'scroll', this._onElementScroll);
            }
        }
    }

    componentWillUnmount() {
        this._clearTimeouts();

        removeEvent(window, 'resize', this._onWindowResize);
        if (this._scrollTarget) { removeEvent(this._scrollTarget, 'scroll', this._onElementScroll); }
        if (this._scrollTimeout) { clearTimeout(this._scrollTimeout); }
    }

    _isInToggleList(list, item) {
        return list === item || Array.isArray(list) && list.includes(item);
    }

    _isReactElement(element) {
        return toArray(element || []).filter(v => v).every(element => React.isValidElement(element));
    }

    _clearTimeouts() {
        if (this._hoverTimeoutID) {
            clearTimeout(this._hoverTimeoutID);
            this._hoverTimeoutID = null;
        }
        if (this._unhoverTimeoutID) {
            clearTimeout(this._unhoverTimeoutID);
            this._unhoverTimeoutID = null;
        }
    }

    _alignPopupToMiddle = () => {
        const
            { arrowed, arrowOffset } = this.props,
            buttonBounds = this.refs.root.getBoundingClientRect();

        if (!this.refs.popup || !this.refs.popup.container.firstElementChild) {
            this.setState({
                arrowSide: arrowed ? Popup.ArrowSide.TOP : null,
                arrowOffset: arrowed ? arrowOffset || buttonBounds.width / 2 : 0,
                left: buttonBounds.left + (document.scrollingElement || document.documentElement).scrollLeft,
                opened: true,
                top: buttonBounds.bottom + (document.scrollingElement || document.documentElement).scrollTop - parseInt(common.borderWidthDefault)
            });

            return;
        }

        const
            popupBounds = this.refs.popup.container.firstElementChild.getBoundingClientRect(),
            sideOffset = (popupBounds.width - buttonBounds.width) / 2;

        if (!arrowed) {
            const
                left = buttonBounds.left + (document.scrollingElement || document.documentElement).scrollLeft - sideOffset,
                right = buttonBounds.right + (document.scrollingElement || document.documentElement).scrollLeft + sideOffset;

            this.setState({
                arrowSide: null,
                arrowOffset: 0,
                left: left > 0 ? left : buttonBounds.left + (document.scrollingElement || document.documentElement).scrollLeft,
                opened: true,
                right: right < window.clientWidth ? right : window.clientWidth,
                top: buttonBounds.bottom + (document.scrollingElement || document.documentElement).scrollTop - parseInt(common.borderWidthDefault)
            });
        }
        else {
            const
                buttonMidPoint = buttonBounds.left + (document.scrollingElement || document.documentElement).scrollLeft + buttonBounds.width / 2,
                offset = sideOffset + buttonBounds.width / 2;

            this.setState({
                arrowSide: Popup.ArrowSide.TOP,
                arrowOffset: arrowOffset || buttonMidPoint / 2 - offset > 0 ? offset : buttonMidPoint - buttonBounds.left - (document.scrollingElement || document.documentElement).scrollLeft,
                left: buttonMidPoint,
                opened: true,
                top: buttonBounds.bottom + (document.scrollingElement || document.documentElement).scrollTop - parseInt(common.borderWidthDefault)
            });
        }
    };

    _tryOpen = (event, force) => {
        this._clearTimeouts();

        if (this.state.opened && !force) {
            return;
        }

        if (event) {
            if (this.props.onOpen) {
                this.props.onOpen(this, event);
            }

            if (this.props.opened !== undefined) {
                return;
            }
        }

        const
            { arrowed, arrowOffset, snap, icon } = this.props,
            { scrollLeft, scrollTop } = document.scrollingElement || document.documentElement,
            buttonBounds = this.refs.root.getBoundingClientRect();

        if (this._isInToggleList(snap, SnapSide.MIDDLE)) {
            this._alignPopupToMiddle();
            return;
        }

        if (!arrowed) {
            this.setState({
                opened: true,
                left: this._isInToggleList(snap, SnapSide.LEFT) ? buttonBounds.left + scrollLeft : null,
                right: this._isInToggleList(snap, SnapSide.RIGHT) ? buttonBounds.right + scrollLeft : null,
                top: buttonBounds.bottom + scrollTop - parseInt(common.borderWidthDefault),
                bottom: null,
                arrowSide: null,
                arrowOffset: 0
            });
        }
        else {
            const
                snapLeft = this._isInToggleList(snap, SnapSide.LEFT),
                snapRight = this._isInToggleList(snap, SnapSide.RIGHT),
                // magic value to make arrow sides stay inside popup's top size, includes half arrow width, borders and some offset
                // it should be handled by Popup itself, but we also need to align Popup sides with button sides, so we have to duplicate it here
                minOffset = 12,
                iconBounds = icon && arrowOffset === undefined ? this.refs.icon.getBoundingClientRect() : null,
                alignedArrowOffset = arrowOffset === undefined || arrowOffset === null ?
                    iconBounds ?
                        iconBounds.left + Math.ceil(iconBounds.width / 2) - buttonBounds.left :
                        '50%' :
                    arrowOffset,
                calculatedArrowOffset = Math.max(minOffset,
                    Math.min(
                        (parseInt(alignedArrowOffset) >= 0 ? 0 : buttonBounds.width) +
                        parseInt(alignedArrowOffset) * (/%/.test(alignedArrowOffset) ? buttonBounds.width / 100 : 1),
                        buttonBounds.width - minOffset
                    )),
                targetLeft = buttonBounds.left + scrollLeft + (buttonBounds.width / 2 > minOffset ? calculatedArrowOffset : buttonBounds.width / 2) + this.props.panelOffset,
                popupArrowOffset = snapLeft ? calculatedArrowOffset : snapRight ? -buttonBounds.width + calculatedArrowOffset : 0,
                minWidth = snapLeft && snapRight ? buttonBounds.width : snapLeft || snapRight ? Math.abs(popupArrowOffset) + minOffset : 0;

            this.setState({
                opened: true,
                left: targetLeft,
                right: targetLeft + Math.max(2 * minOffset, minWidth),
                top: buttonBounds.bottom + scrollTop - parseInt(common.borderWidthDefault),
                bottom: null,
                arrowSide: Popup.ArrowSide.TOP,
                arrowOffset: popupArrowOffset < 0 ? Math.min(-minOffset, popupArrowOffset) : Math.max(minOffset, popupArrowOffset)
            });
        }
    };

    _scrollTarget = null;
    _scrollTimeout = null;
    _contentResizeEvents = [];
    _errorThrown = false;

    _tryClose = (event, force) => {
        this._clearTimeouts();

        if (!this.state.opened && !force) {
            return;
        }

        if (event) {
            if (this.props.onClose) {
                this.props.onClose(this, event);
            }

            if (this.props.opened !== undefined) {
                return;
            }
        }

        this._contentResizeEvents = [];
        this.setState({ opened: false });
    };

    _onWindowResize = () => {
        if (this.state.opened) {
            this._contentResizeEvents = [];
            this._tryOpen(null, true);
        }
    };

    _repositionPopup() {
        const element = this.refs.root;

        if (element) {
            const
                { shouldGoDown } = this.state,
                { scrollTop } = document.scrollingElement || document.documentElement,

                originalButtonBounds = element.getBoundingClientRect(),
                buttonBounds = {
                    ...originalButtonBounds,
                    top: originalButtonBounds.top + parseInt(common.borderWidthDefault),
                    bottom: originalButtonBounds.bottom - parseInt(common.borderWidthDefault)
                };

            let
                top = null,
                bottom = null;

            if (shouldGoDown) {
                top = buttonBounds.bottom + scrollTop;
                if (this.state.bottom) { bottom = this.state.bottom - (this.state.top - top); }
            }
            else {
                bottom = buttonBounds.top + scrollTop;
                if (this.state.top) { top = this.state.top - (this.state.bottom - bottom); }
            }

            if (top !== this.state.top || bottom !== this.state.bottom) {
                this.setState({ top, bottom });
            }
        }
    }

    _onElementScroll = () => {
        if (this._scrollTimeout) { clearTimeout(this._scrollTimeout); }
        setTimeout(this._repositionPopup.bind(this), 100);
    };

    _onContentResize = (sender) => {
        if (this.state.opened && sender) {
            const
                { scrollTop } = document.scrollingElement || document.documentElement,
                windowBounds = { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight, width: window.innerWidth, height: window.innerHeight },
                popupBounds = sender.container.firstElementChild.getBoundingClientRect(),
                arrowOffset = this.props.arrowed ? Popup.ARROW_SIZE + Popup.EDGE_WIDTH + Popup.BORDER_WIDTH : 0; // TODO Temporary fix for arrowed DropPanel freezing the page. Remove once better solution is found.
            if (
                popupBounds.left < windowBounds.left - 1 ||
                popupBounds.right > windowBounds.right + 1 ||
                popupBounds.top < windowBounds.top - 1 - arrowOffset ||
                popupBounds.bottom > windowBounds.bottom + 1 + arrowOffset
            ) {
                const
                    overflowLeft = windowBounds.left - popupBounds.left,
                    overflowRight = popupBounds.right - windowBounds.right,
                    originalButtonBounds = this.refs.root.getBoundingClientRect(),
                    buttonBounds = {
                        ...originalButtonBounds,
                        top: originalButtonBounds.top + parseInt(common.borderWidthDefault),
                        bottom: originalButtonBounds.bottom - parseInt(common.borderWidthDefault)
                    },
                    availableSpace = {
                        top: buttonBounds.top - windowBounds.top,
                        bottom: windowBounds.bottom - buttonBounds.bottom
                    },
                    shouldGoDown = availableSpace.bottom >= popupBounds.height || availableSpace.bottom >= availableSpace.top,
                    oldState = this.state,
                    newState = !this.props.arrowed ?
                        deepAssign(oldState, {
                            shouldGoDown,
                            left: popupBounds.width > windowBounds.width || overflowLeft > 0 ?
                                windowBounds.left :
                                oldState.left !== null && overflowRight > 0 ?
                                    oldState.left - overflowRight :
                                    oldState.left,
                            right: popupBounds.width > windowBounds.width || overflowRight > 0 ?
                                windowBounds.right :
                                oldState.right !== null && overflowLeft > 0 ?
                                    oldState.right + overflowLeft :
                                    oldState.right,
                            top: shouldGoDown ? buttonBounds.bottom + scrollTop : availableSpace.top >= popupBounds.height ? null : windowBounds.top + scrollTop,
                            bottom: !shouldGoDown ? buttonBounds.top + scrollTop : availableSpace.bottom >= popupBounds.height ? null : windowBounds.bottom + scrollTop
                        }) :
                        deepAssign(oldState, {
                            shouldGoDown,
                            top: shouldGoDown ? buttonBounds.bottom + scrollTop : availableSpace.top >= popupBounds.height ? null : windowBounds.top + scrollTop,
                            bottom: !shouldGoDown ? buttonBounds.top + scrollTop : availableSpace.bottom >= popupBounds.height ? null : windowBounds.bottom + scrollTop,
                            arrowSide: shouldGoDown ? Popup.ArrowSide.TOP : Popup.ArrowSide.BOTTOM,
                            arrowOffset: oldState.arrowOffset + (overflowLeft > 0 ? -overflowLeft : overflowRight > 0 ? +overflowRight : 0)
                        });


                if (oldState !== newState) {
                    if (this._contentResizeEvents.length > CONTENT_RESIZE_LIMIT) {
                        if (!this._errorThrown) {
                            this._errorThrown = true;
                            if (DEBUG) { throw new Error('DropPanel content resize limit exceeded. It might be stuck in positioning loop.'); }
                        }

                        return;
                    }
                    else if (this._errorThrown) {
                        this._errorThrown = false;
                    }

                    this.setState(newState);
                    this._contentResizeEvents.push(+new Date());
                }
            }
        }
    };

    _onClick = (event) => {
        if (this.props.onClick) {
            this.props.onClick(this, event);
        }

        if (this._isInToggleList(this.props.openMode, OpenMode.BUTTON)) {
            this._tryOpen(event);
        }
        if (this._isInToggleList(this.props.closeMode, CloseMode.BUTTON)) {
            this._tryClose(event);
        }
    };

    _onKeyDown = (event) => {
        if (this.props.onKeyDown) {
            this.props.onKeyDown(this, event);
        }
    };

    _onMouseOver = (sender, event) => {
        if (sender !== this || isOverEvent(event, this.refs.root)) {
            if (this._isInToggleList(this.props.openMode, OpenMode.HOVER)) {
                if (this.state.opened) {
                    this._clearTimeouts();
                }
                else if (!this._hoverTimeoutID) {
                    this._hoverTimeoutID = setTimeout(() => this._tryOpen(event), this.props.hoverTimeout);
                }
            }
        }
    };

    _onMouseOut = (sender, event) => {
        if (sender !== this || isOutEvent(event, this.refs.root)) {
            if (this._isInToggleList(this.props.closeMode, CloseMode.UNHOVER)) {
                if (!this.state.opened) {
                    this._clearTimeouts();
                }
                else if (!this._unhoverTimeoutID) {
                    this._unhoverTimeoutID = setTimeout(() => this._tryClose(event), this.props.unhoverTimeout);
                }
            }
            else if (this._isInToggleList(this.props.openMode, OpenMode.HOVER) && !this.state.opened) {
                this._clearTimeouts();
            }
        }
    };

    _onPopupClose = (sender, event) => {
        this._tryClose(event);
    };

    render() {
        const { title, disabled, tabIndex } = this.props,
            className = `drop-panel ${this.props.className}`,
            opened = !disabled && this.state.opened,
            icon = this.props.icon !== AUTO_ICON ? this.props.icon : `caret-${opened ? 'up' : 'down'}`,
            handlers = !disabled ?
                {
                    onClick: this._onClick,
                    onKeyDown: this._onKeyDown,
                    onMouseOver: this._onMouseOver.bind(this, this),
                    onMouseOut: this._onMouseOut.bind(this, this)
                } :
                null;

        return (
            <div ref="root" tabIndex={tabIndex} disabled={disabled} {...handlers}
                className={
                    `${className} control inline-flex-row${
                        opened ? ' active' : ''
                    }${disabled ? ' disabled' : ''}`
                }>
                {title ? <span ref="title" className="title main-col">{this._isReactElement(title) ? title : <FormattedResource className="title" resource={title} />}</span> : null}
                {
                    icon ?
                        this._isReactElement(icon) ?
                            <span ref="icon" className="icon">{icon}</span> :
                            <span ref="icon" className={`icon fa fa-${icon}`} /> :
                        null
                }
                {
                    opened ?
                        <Popup ref="popup"
                            {...this.state}
                            dimmed={this.props.dimmed}
                            fixed={false}
                            className={className.replace(/(\S+)/g, '$1-dropdown')}
                            closeMode={toArray(this.props.closeMode).filter(closeMode => closeMode !== CloseMode.BUTTON && closeMode !== CloseMode.UNHOVER)}
                            onMouseOver={this._onMouseOver}
                            onMouseOut={this._onMouseOut}
                            onResize={this._onContentResize}
                            onClose={this._onPopupClose}>
                            {this.props.children}
                        </Popup> :
                        null
                }
            </div >
        );
    }
}
